import React, { memo } from 'react';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';

/**
 * ConcordiaLogo — code-rendered brand logo.
 * variant: 'full' (login, large), 'sidebar' (expanded), 'icon' (collapsed)
 */
export function ConcordiaLogo({ variant = 'full' }) {
  if (variant === 'icon') {
    return (
      <div style={{
        width: 36, height: 36, borderRadius: 7,
        background: '#126463',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <span style={{
          fontFamily: "'Montserrat', sans-serif",
          fontWeight: 900, fontSize: 18, color: '#fff',
          letterSpacing: '-0.02em', lineHeight: 1,
        }}>C</span>
      </div>
    );
  }

  if (variant === 'sidebar') {
    return (
      <div style={{
        background: '#126463',
        borderRadius: 7,
        padding: '6px 10px',
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        lineHeight: 1,
      }}>
        <span style={{
          fontFamily: "'Montserrat', sans-serif",
          fontWeight: 900, fontSize: 13,
          color: '#fff', letterSpacing: '0.07em',
          textTransform: 'uppercase',
        }}>CONCORDIA</span>
        <span style={{
          fontFamily: "'Calibri', 'Trebuchet MS', sans-serif",
          fontWeight: 400, fontSize: 7,
          color: '#F2E1B3', letterSpacing: '0.04em',
          marginTop: 2, whiteSpace: 'nowrap',
        }}>Mining Consulting & Investments</span>
      </div>
    );
  }

  /* variant === 'full' */
  return (
    <img
      src="/logo_800x268.png"
      alt="Concordia Mining Consulting & Investments"
      style={{ width: '100%', maxWidth: 360, height: 'auto', borderRadius: 10, display: 'block' }}
    />
  );
}

const estadoConfig = {
  ACTIVO:      { label: 'Activo',      color: '#1F9D6B', bg: 'rgba(31,157,107,0.10)',  border: 'rgba(31,157,107,0.25)' },
  POR_VENCER:  { label: 'Por Vencer',  color: '#D9892B', bg: 'rgba(217,137,43,0.10)',  border: 'rgba(217,137,43,0.25)' },
  COMPLETADO:  { label: 'Completado',  color: '#2F7BD9', bg: 'rgba(47,123,217,0.10)',  border: 'rgba(47,123,217,0.25)' },
  VENCIDO:     { label: 'Vencido',     color: '#D94A4A', bg: 'rgba(217,74,74,0.10)',   border: 'rgba(217,74,74,0.25)' },
};

export function Badge({ estado }) {
  const cfg = estadoConfig[estado] || estadoConfig.ACTIVO;
  return (
    <span style={{
      display: 'inline-flex', padding: '3px 10px',
      fontSize: 11, fontWeight: 600,
      borderRadius: 99,
      background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.color,
      letterSpacing: '0.3px',
    }}>
      {cfg.label}
    </span>
  );
}

export function KpiCard({ label, value, sub, trend }) {
  const trendColor = trend === 'up' ? 'var(--state-success)' : trend === 'down' ? 'var(--state-danger)' : 'var(--brand-muted)';
  const TrendIcon = trend === 'up' ? ArrowUpRight : trend === 'down' ? ArrowDownRight : Minus;
  return (
    <div style={{
      background: 'var(--surface-card)',
      border: '1px solid var(--border-default)',
      borderTop: '3px solid var(--brand-primary)',
      borderRadius: 'var(--radius-lg)',
      padding: '18px 20px',
      boxShadow: 'var(--shadow-sm)',
    }}>
      <p style={{
        fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em',
        color: 'var(--brand-muted)', marginBottom: 10,
        fontWeight: 600,
      }}>
        {label}
      </p>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{
          fontSize: 36, fontWeight: 600, color: 'var(--brand-ink)',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {value}
        </span>
        {sub && (
          <span style={{ fontSize: 11, color: trendColor, display: 'flex', alignItems: 'center', gap: 2 }}>
            <TrendIcon size={13} /> {sub}
          </span>
        )}
      </div>
    </div>
  );
}

export const LoadingSpinner = memo(function LoadingSpinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60 }}>
      <div style={{
        width: 32, height: 32,
        border: '3px solid var(--border)',
        borderTopColor: 'var(--accent)',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
    </div>
  );
});

export function ErrorMessage({ message, onRetry }) {
  return (
    <div style={{
      padding: '16px 20px',
      background: 'rgba(217,74,74,0.06)',
      border: '1px solid rgba(217,74,74,0.18)',
      borderRadius: 10, color: 'var(--state-danger)',
      fontSize: 13, textAlign: 'center',
    }}>
      <p style={{ marginBottom: onRetry ? 10 : 0 }}>{message}</p>
      {onRetry && (
        <button onClick={onRetry} style={{
          padding: '6px 16px',
          background: 'rgba(217,74,74,0.08)',
          border: '1px solid rgba(217,74,74,0.25)',
          borderRadius: 6, color: 'var(--state-danger)',
          fontSize: 12, cursor: 'pointer',
          fontWeight: 600,
        }}>
          Reintentar
        </button>
      )}
    </div>
  );
}

export const formatCurrency = (v) => '$' + Number(v).toLocaleString('es-CL');
